import { useEffect, useMemo, useState, type ComponentType, type ReactNode } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import { kadryErrorMessage, kadryPlural } from '@entities/kadry';
import {
  useGetKadryOverviewQuery,
  useListKadryBotsQuery,
  useListKadrySourcesQuery,
} from '@shared/api';
import { FallbackBanner } from '@shared/ui/FallbackBanner';
import { Skeleton } from '@shared/ui/Skeleton';
import {
  IconArrowRight,
  IconBellAlert,
  IconCheck,
  IconClipboardList,
  IconInbox,
  IconSparkles,
  IconUsers,
} from '@shared/ui/icons';
import { AttentionPanel, attentionItems } from './AttentionPanel';
import { ChannelsPanel } from './ChannelsPanel';
import { FunnelPanel } from './FunnelPanel';
import { RequestsPanel } from './RequestsPanel';
import { SearchPanel } from './SearchPanel';
import { TrendPanel } from './TrendPanel';
import {
  BigNumber,
  Metric,
  Metrics,
  SectionLabel,
  Tile,
  TileGrid,
  TileHead,
  TileIcon,
  TileNote,
  TileTitle,
  TwoCols,
  fmtHours,
  pct,
} from './ui';

/* Дашборд «Обзор» ОСНОВА.Кадры: что требует внимания, воронка подбора,
   динамика, заявки в работе, каналы и бюджет. Один запрос сводки; пока идёт
   поиск — опрос каждые 10 с, иначе раз в минуту (плюс WS-инвалидация при
   ответах кандидатов). */

type IconType = ComponentType<{ size?: number }>;

const REQUESTS = '/agents/kadry/requests';
const INTERESTED = '/agents/kadry/interested';
/** Якорь таблицы «Заявки в работе» — разбивка показателей по заявкам. */
const REQUESTS_ANCHOR = 'kadry-overview-requests';

function scrollToRequests() {
  document.getElementById(REQUESTS_ANCHOR)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
}

const Arrow = styled.div`
  position: absolute;
  top: ${({ theme }) => theme.spacing(5)};
  right: ${({ theme }) => theme.spacing(5)};
  display: flex;
  color: ${({ theme }) => theme.colors.text.muted};
  transition: transform ${({ theme }) => theme.motion.base} ${({ theme }) => theme.motion.spring};

  ${Tile}:hover & {
    transform: translateX(4px);
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const Head = styled.div`
  flex: 1;
  padding-right: ${({ theme }) => theme.spacing(6)};
`;

const SkeletonTile = styled.div`
  min-height: 164px;
  padding: ${({ theme }) => theme.spacing(5)};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.lg};
  background: ${({ theme }) => theme.colors.backgroundElevated};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(4)};
`;

interface TileProps {
  delay: number;
  icon: IconType;
  title: string;
  note: string;
  /** Страница перехода или действие (прокрутка к разбивке по заявкам). */
  to: string | (() => void);
  children: ReactNode;
}

function StatTile({ delay, icon: Icon, title, note, to, children }: TileProps) {
  const navigate = useNavigate();
  return (
    <Tile type="button" $delay={delay} onClick={() => (typeof to === 'string' ? navigate(to) : to())}>
      <TileHead>
        <TileIcon>
          <Icon size={19} />
        </TileIcon>
        <Head>
          <TileTitle>{title}</TileTitle>
          <TileNote>{note}</TileNote>
        </Head>
      </TileHead>
      {children}
      <Arrow>
        <IconArrowRight size={18} />
      </Arrow>
    </Tile>
  );
}

function LoadingGrid() {
  return (
    <TileGrid>
      {Array.from({ length: 6 }, (_, i) => (
        <SkeletonTile key={i}>
          <Skeleton $width={140} $height={16} />
          <Skeleton $width={72} $height={32} />
          <Skeleton $width={180} $height={12} />
        </SkeletonTile>
      ))}
    </TileGrid>
  );
}

export function KadryDashboard({ admin }: { admin: boolean }) {
  const [pollMs, setPollMs] = useState(60_000);
  const { data: o, isLoading, error } = useGetKadryOverviewQuery(undefined, {
    pollingInterval: pollMs,
    skipPollingIfUnfocused: true,
  });
  const running = o?.running.length ?? 0;
  useEffect(() => {
    setPollMs(running > 0 ? 10_000 : 60_000);
  }, [running]);
  // Готовность подключений — только администратору подбора.
  const { data: sources } = useListKadrySourcesQuery(undefined, { skip: !admin });
  const { data: bots } = useListKadryBotsQuery(undefined, { skip: !admin });
  const items = useMemo(
    () => (o ? attentionItems(o, admin ? { sources, bots } : null) : []),
    [o, admin, sources, bots],
  );

  if (isLoading) return <LoadingGrid />;
  if (error || !o) {
    return (
      <FallbackBanner tone="danger" title="Показатели не загрузились">
        {kadryErrorMessage(error)}
      </FallbackBanner>
    );
  }

  const { requests: rq, funnel: f, responses: r } = o;
  const answered = r.interested + r.not_interested;
  const open = rq.active + rq.paused + rq.draft;

  return (
    <>
      <AttentionPanel items={items} />

      <SectionLabel>Сейчас</SectionLabel>
      <SearchPanel s={o.search} running={o.running} />

      <SectionLabel>Подбор</SectionLabel>
      <TileGrid>
        <StatTile delay={0} icon={IconClipboardList} title="Заявки" note="в работе" to={REQUESTS}>
          <BigNumber>{rq.active}</BigNumber>
          <Metrics>
            <Metric>
              Ищем человек
              <strong>{rq.open_positions}</strong>
            </Metric>
            <Metric>
              Черновики
              <strong>{rq.draft}</strong>
            </Metric>
            <Metric $tone={rq.paused > 0 ? 'warning' : undefined}>
              На паузе
              <strong>{rq.paused}</strong>
            </Metric>
          </Metrics>
        </StatTile>

        <StatTile delay={40} icon={IconUsers} title="Найдено кандидатов" note="на площадках по заявкам" to={scrollToRequests}>
          <BigNumber>{f.found}</BigNumber>
          <Metrics>
            <Metric $tone={f.found_7d > 0 ? 'success' : undefined}>
              За 7 дней
              <strong>{f.found_7d > 0 ? `+${f.found_7d}` : 0}</strong>
            </Metric>
            <Metric>
              Прошли фильтры
              <strong>{f.passed_filters}</strong>
            </Metric>
          </Metrics>
        </StatTile>

        <StatTile delay={80} icon={IconSparkles} title="Анализ резюме" note="DeepSeek без персональных данных" to={scrollToRequests}>
          <BigNumber>{f.analyzed_ai}</BigNumber>
          <Metrics>
            <Metric>
              Правилами
              <strong>{f.analyzed_other}</strong>
            </Metric>
            <Metric>
              По краткой выдаче
              <strong>{f.preliminary}</strong>
            </Metric>
          </Metrics>
        </StatTile>

        <StatTile delay={120} icon={IconInbox} title="Предложения" note="hh.ru, Telegram, MAX" to={scrollToRequests}>
          <BigNumber>{f.offered}</BigNumber>
          <Metrics>
            <Metric>
              За 7 дней
              <strong>{r.offered_7d}</strong>
            </Metric>
            <Metric $tone={r.awaiting > 0 ? 'warning' : undefined}>
              Ждут ответа
              <strong>{r.awaiting}</strong>
            </Metric>
          </Metrics>
        </StatTile>

        <StatTile delay={160} icon={IconCheck} title="Ответили «Подходит»" note="кандидат готов к разговору" to={INTERESTED}>
          <BigNumber $tone={r.interested > 0 ? 'success' : undefined}>{r.interested}</BigNumber>
          <Metrics>
            <Metric>
              «Не подходит»
              <strong>{r.not_interested}</strong>
            </Metric>
            <Metric>
              Ответили
              <strong>{pct(answered, f.offered)}</strong>
            </Metric>
            <Metric>
              Ответ за
              <strong>{fmtHours(r.median_response_hours)}</strong>
            </Metric>
          </Metrics>
        </StatTile>

        <StatTile
          delay={200}
          icon={IconBellAlert}
          title="Связь с кандидатами"
          note="после ответа «Подходит»"
          to={`${INTERESTED}?show=${r.not_contacted > 0 ? 'not_contacted' : 'waiting'}`}
        >
          <BigNumber>{f.contacted}</BigNumber>
          <Metrics>
            <Metric $tone={r.waiting_call > 0 ? 'warning' : undefined}>
              Ждут звонка
              <strong>{r.waiting_call}</strong>
            </Metric>
            <Metric $tone={r.not_contacted > 0 ? 'danger' : undefined}>
              Не связались
              <strong>{r.not_contacted}</strong>
            </Metric>
            <Metric>
              Звонок за
              <strong>{fmtHours(r.median_contact_hours)}</strong>
            </Metric>
          </Metrics>
        </StatTile>
      </TileGrid>

      <SectionLabel>Воронка и динамика</SectionLabel>
      <TwoCols>
        <FunnelPanel f={f} delay={80} />
        <TrendPanel days={o.trend} delay={140} />
      </TwoCols>

      <SectionLabel id={REQUESTS_ANCHOR}>
        Заявки · {open} {kadryPlural(open, ['открытая', 'открытые', 'открытых'])}
      </SectionLabel>
      <RequestsPanel rows={o.top_requests} total={open} delay={100} />

      <SectionLabel>Каналы{o.budgets.length > 0 ? ' и бюджеты' : ''}</SectionLabel>
      <ChannelsPanel o={o} delay={120} />
    </>
  );
}
